<template>
  <div class="bg-white border rounded-lg p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <h3 class="text-lg font-medium" v-html="chartTitle"></h3>
      <el-button
        type="text"
        icon="el-icon-close"
        @click="$emit('close')"
        class="text-gray-500 hover:text-gray-700"
      ></el-button>
    </div>

    <!-- Bound Controls -->
    <div
      class="
        bound-controls
        mb-8
        p-4
        bg-gray-50
        rounded-lg
        border border-gray-200
      "
    >
      <div class="flex items-center gap-2 mb-3">
        <i class="el-icon-info text-blue-500"></i>
        <span class="text-sm text-gray-600">
          مقادیر را وارد کنید
          <span class="hidden-on-mobile">
            یا روی دکمه «رسم روی نمودار» کلیک کنید، سپس روی نمودار کلیک کنید
          </span>
        </span>
      </div>

      <!-- Drawing mode banner -->
      <div
        v-if="drawingMode"
        class="drawing-mode-banner mb-3 p-2 rounded-lg flex items-center gap-2"
        :class="
          drawingMode === 'upper'
            ? 'bg-red-100 border border-red-300'
            : 'bg-blue-100 border border-blue-300'
        "
      >
        <i
          class="el-icon-aim"
          :class="drawingMode === 'upper' ? 'text-red-600' : 'text-blue-600'"
          style="font-size: 18px"
        ></i>
        <span
          class="text-sm font-medium"
          :class="drawingMode === 'upper' ? 'text-red-700' : 'text-blue-700'"
        >
          {{
            drawingMode === 'upper'
              ? 'روی نمودار کلیک کنید تا حد بالایی مشخص شود'
              : 'روی نمودار کلیک کنید تا حد پایینی مشخص شود'
          }}
        </span>
        <el-button
          size="mini"
          type="text"
          @click="drawingMode = null"
          class="mr-auto text-gray-500"
        >
          لغو
        </el-button>
      </div>

      <div class="flex flex-wrap gap-8 items-end">
        <div class="bound-input-group">
          <label class="block text-sm font-medium text-red-600 mb-1">
            <i class="el-icon-top"></i>
            حد بالایی (Upper Bound)
          </label>
          <div class="flex items-center gap-2">
            <el-button
              size="medium"
              :type="drawingMode === 'upper' ? 'danger' : 'default'"
              :plain="drawingMode !== 'upper'"
              @click="drawingMode = drawingMode === 'upper' ? null : 'upper'"
              style="padding: 5px 8px"
              class="hidden-on-mobile"
              title="رسم روی نمودار بالایی"
            >
              <i class="el-icon-aim"></i>
            </el-button>
            <el-input
              :value="upperBoundStr"
              @input="onBoundInput('upper', $event)"
              size="small"
              placeholder="مثال: 8"
              style="width: 110px"
              dir="ltr"
            />
          </div>
        </div>

        <div class="bound-input-group">
          <label class="block text-sm font-medium text-blue-600 mb-1">
            <i class="el-icon-bottom"></i>
            حد پایینی (Lower Bound)
          </label>
          <div class="flex items-center gap-2">
            <el-button
              size="medium"
              :type="drawingMode === 'lower' ? 'primary' : 'default'"
              :plain="drawingMode !== 'lower'"
              @click="drawingMode = drawingMode === 'lower' ? null : 'lower'"
              style="padding: 5px 8px"
              class="hidden-on-mobile"
              title="رسم روی نمودار پایینی"
            >
              <i class="el-icon-aim"></i>
            </el-button>
            <el-input
              :value="lowerBoundStr"
              @input="onBoundInput('lower', $event)"
              size="small"
              placeholder="مثال: 2"
              style="width: 110px"
              dir="ltr"
            />
          </div>
        </div>

        <el-button
          v-if="upperBound !== null || lowerBound !== null"
          size="small"
          type="danger"
          plain
          @click="clearBounds"
        >
          <i class="el-icon-delete"></i>
          پاک کردن خطوط
        </el-button>
      </div>
    </div>

    <!-- Stats Panel -->
    <div
      v-if="
        (upperBound !== null || lowerBound !== null) &&
        boundStats.totalMinutes > 0
      "
      class="stats-panel mb-8 grid grid-cols-1 gap-3"
      :class="boundStats.statsGridClass"
    >
      <div
        v-if="upperBound !== null"
        class="
          stat-card
          bg-red-50
          border border-red-200
          rounded-lg
          p-3
          flex
          items-center
          gap-3
        "
      >
        <div class="stat-icon bg-red-100 rounded-full p-2">
          <i class="el-icon-top text-red-600" style="font-size: 20px"></i>
        </div>
        <div>
          <div class="text-xs text-red-500 mb-1">
            دقایق بالاتر از حد مجاز ({{ upperBound }}°C)
          </div>
          <div class="text-2xl font-bold text-red-600">
            {{ boundStats.minutesOverUpper }}
          </div>
          <div class="text-xs text-gray-500">
            دقیقه از {{ boundStats.totalMinutes }} دقیقه
          </div>
        </div>
        <div class="mr-auto">
          <el-progress
            type="circle"
            :percentage="
              Math.round(
                (boundStats.minutesOverUpper / boundStats.totalMinutes) * 100
              )
            "
            :stroke-width="6"
            :width="50"
            status="exception"
          />
        </div>
      </div>

      <div
        v-if="lowerBound !== null"
        class="
          stat-card
          bg-blue-50
          border border-blue-200
          rounded-lg
          p-3
          flex
          items-center
          gap-3
        "
      >
        <div class="stat-icon bg-blue-100 rounded-full p-2">
          <i class="el-icon-bottom text-blue-600" style="font-size: 20px"></i>
        </div>
        <div>
          <div class="text-xs text-blue-500 mb-1">
            دقایق پایین‌تر از حد مجاز ({{ lowerBound }}°C)
          </div>
          <div class="text-2xl font-bold text-blue-600">
            {{ boundStats.minutesUnderLower }}
          </div>
          <div class="text-xs text-gray-500">
            دقیقه از {{ boundStats.totalMinutes }} دقیقه
          </div>
        </div>
        <div class="mr-auto">
          <el-progress
            type="circle"
            :percentage="
              Math.round(
                (boundStats.minutesUnderLower / boundStats.totalMinutes) * 100
              )
            "
            :stroke-width="6"
            :width="50"
          />
        </div>
      </div>
    </div>

    <!-- Arrival Times Tags -->
    <div
      v-if="arrivalDatetimes.length > 0"
      class="arrival-info mb-8 flex flex-wrap gap-2 items-center"
    >
      <span class="text-sm font-medium text-gray-600">
        <i class="el-icon-location text-green-600"></i>
        زمان‌های رسیدن راننده:
      </span>
      <el-tag
        v-for="(dt, idx) in arrivalDatetimes"
        :key="idx"
        type="success"
        size="medium"
        effect="plain"
      >
        {{ formatArrivalTime(dt) }}
      </el-tag>
    </div>

    <!-- Loading -->
    <div v-if="chartLoading" class="text-center py-8">
      <i class="el-icon-loading text-2xl"></i>
      <p class="mt-2">{{ $t('LOADING') }}...</p>
    </div>

    <!-- Chart -->
    <div v-else>
      <div class="mb-4" :class="{ 'chart-drawing-mode': drawingMode }">
        <VueApexCharts
          v-if="chartData.length > 0"
          :key="chartKey"
          ref="chart"
          type="area"
          height="380"
          :options="computedChartOptions"
          :series="chartData"
        />
        <div v-else class="text-center py-8 text-gray-500">
          {{ $t('NO_DATA') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { toEnglish } from '@/mixins/toEnglish'

export default {
  name: 'TemperatureChart',

  mixins: [toEnglish],

  components: { VueApexCharts },

  props: {
    chartTitle: { type: String, default: '' },
    chartData: { type: Array, default: () => [] },
    chartLoading: { type: Boolean, default: false },
    rawTemperatureData: { type: Array, default: () => [] },
    arrivalDatetimes: { type: Array, default: () => [] },
  },

  data() {
    return {
      upperBound: null,
      lowerBound: null,
      upperBoundStr: '',
      lowerBoundStr: '',
      drawingMode: null,
    }
  },

  watch: {
    chartData() {
      this.redrawChart()
    },

    upperBound() {
      this.redrawChart()
    },

    lowerBound() {
      this.redrawChart()
    },

    arrivalDatetimes() {
      this.redrawChart()
    },
  },

  mounted() {
    this.redrawChart()
  },

  computed: {
    boundStats() {
      if (!this.rawTemperatureData.length) {
        return {
          minutesOverUpper: 0,
          minutesUnderLower: 0,
          totalMinutes: 0,
          statsGridClass: '',
        }
      }

      const minuteMap = {}
      this.rawTemperatureData.forEach((point) => {
        const minuteTs = this.toMinuteTimestamp(point.datetime)
        if (!minuteMap[minuteTs]) minuteMap[minuteTs] = []
        minuteMap[minuteTs].push(point.value)
      })

      const totalMinutes = Object.keys(minuteMap).length
      let minutesOverUpper = 0
      let minutesUnderLower = 0

      Object.values(minuteMap).forEach((values) => {
        const avg = values.reduce((a, b) => a + b, 0) / values.length
        if (this.upperBound !== null && avg > this.upperBound)
          minutesOverUpper++
        if (this.lowerBound !== null && avg < this.lowerBound)
          minutesUnderLower++
      })

      const count =
        (this.upperBound !== null ? 1 : 0) + (this.lowerBound !== null ? 1 : 0)
      return {
        minutesOverUpper,
        minutesUnderLower,
        totalMinutes,
        statsGridClass: count === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-1',
      }
    },

    chartKey() {
      // vue-apexcharts updates the `options` and `series` props via separate
      // watchers. On first load both change together, but the options watcher
      // (carrying our annotations) can fire before the series watcher, so
      // annotations get positioned against a stale/empty domain and only
      // "appear" once the user interacts (e.g. zoom) and forces a recompute.
      // Forcing a full remount when a new dataset loads makes ApexCharts init
      // with series + annotations together, avoiding that race entirely.
      if (!this.chartData.length || !this.chartData[0]?.data?.length)
        return 'empty'
      const data = this.chartData[0].data
      const arrivalKey = this.arrivalDatetimes
        .map((dt) => this.toChartTimestamp(dt))
        .join(',')
      return `${data[0].x}-${data[data.length - 1].x}-${
        data.length
      }-${arrivalKey}`
    },

    computedChartOptions() {
      const annotations = { yaxis: [], xaxis: [] }

      if (this.upperBound !== null) {
        annotations.yaxis.push({
          y: this.upperBound,
          borderColor: '#ef4444',
          borderWidth: 2,
          strokeDashArray: 6,
          label: {
            borderColor: '#ef4444',
            style: {
              color: '#fff',
              background: '#ef4444',
              fontSize: '12px',
              fontFamily: 'Vazir',
              padding: { top: 4, bottom: 4, left: 8, right: 8 },
            },
            text: `حد بالایی: ${this.upperBound}°C`,
            position: 'left',
            offsetX: 10,
          },
        })
      }

      if (this.lowerBound !== null) {
        annotations.yaxis.push({
          y: this.lowerBound,
          borderColor: '#3b82f6',
          borderWidth: 2,
          strokeDashArray: 6,
          label: {
            borderColor: '#3b82f6',
            style: {
              color: '#fff',
              background: '#3b82f6',
              fontSize: '12px',
              fontFamily: 'Vazir',
              padding: { top: 4, bottom: 4, left: 8, right: 8 },
            },
            text: `حد پایینی: ${this.lowerBound}°C`,
            position: 'left',
            offsetX: 10,
          },
        })
      }

      annotations.position = 'front'
      this.arrivalDatetimes.forEach((dt, idx) => {
        annotations.xaxis.push({
          x: this.toChartTimestamp(dt),
          borderColor: '#16a34a',
          borderWidth: 2,
          strokeDashArray: 4,
        })
      })

      return {
        chart: {
          id: 'temperature-chart',
          height: 380,
          stacked: false,
          toolbar: {
            show: true,
            autoSelected: 'zoom',
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
          zoom: { type: 'x', enabled: true, autoScaleYaxis: true },
          fontFamily: 'Vazir',
          animations: { enabled: true, easing: 'easeinout', speed: 600 },
          events: {
            click: (event, chartContext) =>
              this.onChartClick(event, chartContext),
          },
        },
        annotations,
        dataLabels: { enabled: false },
        markers: { size: 0, hover: { size: 4 } },
        stroke: {
          curve: 'smooth',
          width: 2.5,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 0.2,
            inverseColors: false,
            opacityFrom: 0.3,
            opacityTo: 0.02,
            stops: [0, 85, 100],
          },
        },
        xaxis: {
          type: 'datetime',
          tickAmount: 8,
          labels: {
            datetimeUTC: false,
            formatter: (value) => {
              const d = new Date(value)
              return `${String(d.getHours()).padStart(2, '0')}:${String(
                d.getMinutes()
              ).padStart(2, '0')}`
            },
            style: { fontSize: '12px' },
            rotate: -30,
          },
          title: { text: 'زمان', style: { fontSize: '13px', fontWeight: 600 } },
        },
        yaxis: {
          title: {
            text: 'دما (°C)',
            style: { fontSize: '13px', fontWeight: 600 },
          },
          labels: {
            formatter: (v) => (v !== undefined ? v.toFixed(1) + '°C' : ''),
          },
        },
        tooltip: {
          shared: false,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const point = w.globals.initialSeries[seriesIndex]?.data?.[dataPointIndex]
            if (!point) return ''
            const value = series[seriesIndex]?.[dataPointIndex]
            const timeText = this.formatPointTime(point.x)
            const tempText =
              value !== null && value !== undefined ? `${Number(value).toFixed(2)}°C` : '-'
            const arrivals = this.getArrivalsForMinute(point.x)
            const arrivalsHtml = arrivals
              .map(
                (t) =>
                  `<span style="display:inline-block;padding:2px 8px;border-radius:999px;background:#dcfce7;color:#166534;font-size:11px;font-weight:600;">${t}</span>`
              )
              .join(' ')

            const arrivalSection = arrivalsHtml
              ? `<div style="margin-top:8px;padding-top:7px;border-top:1px solid #e5e7eb;" class="flex items-center justify-between">
                  <div style="font-size:11px;color:#6b7280;margin-bottom:4px;">زمان رسیدن</div>
                  <div style="display:flex;flex-wrap:wrap;gap:4px;">${arrivalsHtml}</div>
                </div>`
              : ''

            return `<div style="min-width:185px;padding:10px 12px;direction:rtl;font-family:Vazir;line-height:1.5;">
                <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;">
                  <span style="font-size:11px;color:#6b7280;">زمان</span>
                  <span style="font-size:12px;font-weight:700;color:#111827;">${timeText}</span>
                </div>
                <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:4px;">
                  <span style="font-size:11px;color:#6b7280;">دما</span>
                  <span style="font-size:13px;font-weight:700;color:#111827;">${tempText}</span>
                </div>
                ${arrivalSection}
              </div>`
          },
        },
        grid: {
          borderColor: '#e7e7e7',
          row: { colors: ['#f9fafb', 'transparent'], opacity: 0.4 },
          padding: { top: 40 },
        },
        colors: ['#008FFB'],
        legend: { show: true, position: 'top', horizontalAlign: 'left' },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { height: 260 },
              xaxis: { labels: { rotate: -60 } },
            },
          },
        ],
      }
    },
  },

  methods: {
    // Convert Persian/Arabic/Unicode digits and minus variants to ASCII equivalents
    convertDigits(val) {
      let s = String(val)
        .replace(/[۰-۹]/g, (c) => String(c.charCodeAt(0) - '۰'.charCodeAt(0)))
        .replace(/[٠-٩]/g, (c) => String(c.charCodeAt(0) - '٠'.charCodeAt(0)))
        .replace(/[٫،]/g, '.') // Arabic decimal & thousands sep → dot
        .replace(/[\u2212\u002D\u2010\u2011]/g, '-') // all minus/hyphen variants → ASCII minus

      // Keep leading minus, digits, and at most one dot
      const isNeg = s.startsWith('-')
      s = s.replace(/[^0-9.]/g, '') // strip anything not digit or dot
      const dotIdx = s.indexOf('.')
      if (dotIdx !== -1) {
        // collapse multiple dots
        s = s.slice(0, dotIdx + 1) + s.slice(dotIdx + 1).replace(/\./g, '')
      }
      return isNeg ? '-' + s : s
    },

    setBound(type, value) {
      const str = value !== null ? String(value) : ''
      if (type === 'upper') {
        this.upperBound = value
        this.upperBoundStr = str
      } else {
        this.lowerBound = value
        this.lowerBoundStr = str
      }
    },

    onBoundInput(type, raw) {
      const converted = this.convertDigits(raw)
      if (type === 'upper') this.upperBoundStr = converted
      else this.lowerBoundStr = converted

      // Parse only when the string represents a complete number (not mid-typing "-" or "12.")
      const incomplete =
        converted === '' || converted === '-' || converted.endsWith('.')
      const num = parseFloat(converted)
      const numericValue = !incomplete && !isNaN(num) ? num : null

      if (type === 'upper') this.upperBound = numericValue
      else this.lowerBound = numericValue
    },

    clearBounds() {
      this.upperBound = null
      this.upperBoundStr = ''
      this.lowerBound = null
      this.lowerBoundStr = ''
      this.drawingMode = null
    },

    resetState() {
      this.upperBound = null
      this.upperBoundStr = ''
      this.lowerBound = null
      this.lowerBoundStr = ''
      this.drawingMode = null
    },

    normalizeTimestampMs(dt) {
      const raw = Number(dt)
      if (Number.isFinite(raw)) {
        return raw < 1e12 ? raw * 1000 : raw
      }
      return new Date(dt).getTime()
    },

    toChartTimestamp(dt) {
      return this.normalizeTimestampMs(dt)
    },

    toMinuteTimestamp(dt) {
      return Math.floor(this.toChartTimestamp(dt) / 60000) * 60000
    },

    formatArrivalTime(dt) {
      return new Date(this.normalizeTimestampMs(dt)).toLocaleTimeString('fa-IR', {
        timeZone: 'Asia/Tehran',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    },

    formatPointTime(dt) {
      return new Date(this.normalizeTimestampMs(dt)).toLocaleTimeString('fa-IR', {
        timeZone: 'Asia/Tehran',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    },

    getArrivalsForMinute(pointX) {
      const pointMinuteTs = Math.floor(this.normalizeTimestampMs(pointX) / 60000) * 60000
      return this.arrivalDatetimes
        .filter((dt) => this.toMinuteTimestamp(dt) === pointMinuteTs)
        .map((dt) => this.formatArrivalTime(dt))
    },

    // Annotation pixel positions are computed against the chart's layout at
    // render time. If that happens before the container has its final width
    // (e.g. right after mount, or right after new data/bounds/arrivals are
    // set), annotations end up misplaced/invisible until something (like a
    // zoom) forces ApexCharts to recompute — so we force that recompute
    // ourselves shortly after each relevant change.
    redrawChart() {
      clearTimeout(this._redrawTimer)
      this._redrawTimer = setTimeout(() => {
        this.$nextTick(() => {
          if (this.$refs.chart) {
            this.$refs.chart.updateOptions(
              this.computedChartOptions,
              true,
              true,
              true
            )
          }
        })
      }, 200)
    },

    onChartClick(event, chartContext) {
      if (!this.drawingMode) return
      try {
        const globals = chartContext.w.globals
        const plotTop = globals.translateY
        const plotHeight = globals.gridHeight
        const minY = globals.minY
        const maxY = globals.maxY
        const offsetY = event.offsetY

        if (offsetY < plotTop || offsetY > plotTop + plotHeight) return

        const yValue = maxY - ((offsetY - plotTop) / plotHeight) * (maxY - minY)
        const rounded = parseFloat(yValue.toFixed(1))

        this.setBound(this.drawingMode, rounded)
      } catch (e) {
        console.error('Chart click error:', e)
      } finally {
        this.drawingMode = null
      }
    },
  },
}
</script>

<style scoped>
.hidden-on-mobile {
  @media (max-width: 1024px) {
    display: none !important;
  }
}

.bound-input-group {
  display: flex;
  flex-direction: column;
}

.stat-card {
  transition: box-shadow 0.2s;
}

.stat-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart-drawing-mode {
  cursor: crosshair !important;
}

.chart-drawing-mode :deep(.apexcharts-canvas) {
  cursor: crosshair !important;
}

.chart-drawing-mode :deep(.apexcharts-inner) {
  cursor: crosshair !important;
}
</style>
